// src/components/profile/BadgeEarnedPopup.jsx
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Import your sound file - comment this out if you don't have the sound file
import congratsSound from '../../assets/sounds/badge-earned.mp3';

const BadgeEarnedPopup = ({ badge, isVisible, onClose }) => {
  const audioRef = useRef(null);
  
  useEffect(() => {
    if (isVisible && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(error => {
        console.log('Audio playback error:', error);
      });
      
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  return (
    <AnimatePresence>
      {isVisible && badge && (
        <>
          <audio ref={audioRef} src={congratsSound} preload="auto" /> 
          
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ 
                type: "spring", 
                damping: 20, 
                stiffness: 300 
              }}
              className="relative max-w-md w-full mx-4"
            >
              <motion.div
                className={`${badge.color} backdrop-blur-md rounded-xl p-6 shadow-xl border border-white/10 overflow-hidden`}
                initial={{ boxShadow: "0 0 0px rgba(255,255,255,0)" }}
                animate={{ boxShadow: "0 0 30px rgba(255,255,255,0.2)" }}
                transition={{ delay: 0.3, duration: 1 }}
              >
                <button 
                  onClick={onClose}
                  className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="flex flex-col items-center text-center">
                  <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mb-2 text-white/90 font-medium"
                  >
                    🎉 Badge Earned! 🎉
                  </motion.div>
                  
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      delay: 0.3, 
                      type: "spring", 
                      damping: 12, 
                      stiffness: 200 
                    }}
                    className="w-24 h-24 rounded-full flex items-center justify-center mb-4 bg-gray-900/40 p-4"
                  >
                    {badge.icon}
                  </motion.div>
                  
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-xl font-bold text-white mb-1"
                  >
                    {badge.name}
                  </motion.h3>
                  
                  {badge.tierLabel && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 }}
                      className="px-3 py-1 rounded-full bg-white/10 text-white/90 text-xs font-medium mb-3"
                    >
                      {badge.tierLabel}
                    </motion.span>
                  )}
                  
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    className="text-white/80 text-sm"
                  >
                    {badge.description}
                  </motion.p>
                </div>
                
                {/* Animated confetti/sparkles effect */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {[...Array(20)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ 
                        x: Math.random() * 100 - 50 + "%", 
                        y: -20, 
                        opacity: 0,
                        scale: 0
                      }}
                      animate={{ 
                        y: `${100 + Math.random() * 50}%`, 
                        opacity: [0, 1, 0],
                        scale: [0, 1, 0.5]
                      }}
                      transition={{ 
                        duration: 2 + Math.random() * 2,
                        delay: 0.3 + Math.random() * 0.5,
                        repeat: Infinity,
                        repeatDelay: Math.random() * 3
                      }}
                      className="absolute w-1.5 h-1.5 rounded-full bg-white"
                      style={{
                        background: `hsl(${Math.random() * 360}, 80%, 60%)`
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BadgeEarnedPopup;
